import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertCircle, CalendarClock, MapPin, ChevronRight, Hand, Play } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Hero } from "@/pages/Home";
import { StatusBadge, PriorityBadge, OverdueBadge, Empty } from "@/components/common";
import { fmtDT, fmtWhen } from "@/lib/format";
import { cn } from "@/lib/utils";
import { PhotoActionDialog } from "@/components/PhotoActionDialog";

const QUICK = {
  assegnato: { action: "take", label: "Prendi in carico", icon: Hand },
  riaperto: { action: "take", label: "Prendi in carico", icon: Hand },
  preso_in_carico: { action: "start", label: "Inizia il lavoro", icon: Play },
};

const WorkCard = ({ o, onDone }) => {
  const q = QUICK[o.status];
  const [photo, setPhoto] = useState(false);
  const run = async () => {
    if (q.action === "start") {
      setPhoto(true);
      return;
    }
    try {
      await api.post(`/orders/${o.id}/action`, { action: q.action });
      toast.success(q.label + ": fatto");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div data-testid={`mywork-card-${o.code}`} className={cn("card-soft p-4 fade-up", o.is_overdue && "ring-2 ring-red-500")}>
      <Link to={`/ordini/${o.id}`} className="block">
        <div className="flex items-start justify-between gap-2">
          <span className="font-mono text-xs text-muted-foreground">{o.code}</span>
          <PriorityBadge priority={o.priority} />
        </div>
        <p className="mt-1 text-lg font-bold leading-snug">{o.title}</p>
        <p className="mt-2 flex items-center gap-1.5 text-sm text-muted-foreground"><MapPin className="h-4 w-4" />{o.cimitero_name} · {o.area || "—"}</p>
        <p className={cn("mt-1 flex items-center gap-1.5 text-sm", o.is_overdue ? "font-bold text-red-600" : "text-muted-foreground")}><CalendarClock className="h-4 w-4" />{o.due_current ? `Scadenza ${fmtDT(o.due_current)}` : fmtWhen(o)}</p>
        <div className="mt-3 flex flex-wrap gap-2"><StatusBadge status={o.status} /><OverdueBadge order={o} /></div>
      </Link>
      <div className="mt-4 grid gap-2">
        {q && (
          <button onClick={run} data-testid={`mywork-quick-${q.action}-${o.code}`} className="flex min-h-[56px] items-center justify-center gap-2 rounded-xl bg-primary text-lg font-bold text-white tile-press">
            <q.icon className="h-5 w-5" /> {q.label}
          </button>
        )}
        <Link to={`/ordini/${o.id}`} data-testid={`mywork-open-${o.code}`} className="flex min-h-[52px] items-center justify-center gap-1 rounded-xl border bg-white text-base font-semibold">
          Apri ordine <ChevronRight className="h-4 w-4" />
        </Link>
      </div>
      {photo && <PhotoActionDialog order={o} action="start" onClose={() => setPhoto(false)} onDone={onDone} />}
    </div>
  );
};

export default function MyWork() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [assets, setAssets] = useState([]);
  const load = useCallback(() => {
    api.get("/dashboard/my").then((r) => setData(r.data));
    api.get("/assets", { params: { mine: true } }).then((r) => setAssets(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  return (
    <div data-testid="mywork-page">
      <Hero user={user} subtitle="I tuoi incarichi di oggi" />
      <div className="mb-6 grid grid-cols-2 gap-3">
        <div className={cn("rounded-2xl p-4", data?.overdue ? "bg-[#f43b30] text-white" : "card-soft")} data-testid="mywork-overdue-tile">
          <AlertCircle className="h-6 w-6" /><p className="mt-2 text-4xl font-extrabold">{data?.overdue ?? "–"}</p><p className="text-sm font-medium opacity-90">Scaduti</p>
        </div>
        <div className="rounded-2xl bg-[#ff9500] p-4 text-white" data-testid="mywork-today-tile">
          <CalendarClock className="h-6 w-6" /><p className="mt-2 text-4xl font-extrabold">{data?.today ?? "–"}</p><p className="text-sm font-medium opacity-90">In scadenza oggi</p>
        </div>
      </div>
      <h2 className="mb-3 text-lg font-bold">I miei ordini</h2>
      {data && data.orders.length === 0 && <Empty text="Nessun incarico aperto." testid="mywork-empty" />}
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {data?.orders.map((o) => <WorkCard key={o.id} o={o} onDone={load} />)}
      </div>
      {assets.length > 0 && (
        <>
          <h2 className="mb-3 mt-8 text-lg font-bold">Mezzi e attrezzature in custodia</h2>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {assets.map((a) => (
              <Link key={a.id} to={`/mezzi/${a.id}`} className={cn("card-soft flex min-h-[64px] items-center gap-3 p-4", a.status === "guasto" && "ring-2 ring-red-500")} data-testid={`mywork-asset-${a.code}`}>
                <span className="font-mono font-bold">{a.code}</span><span className="flex-1 truncate text-sm">{a.brand} {a.model}</span>
                {a.possession_pending && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-800">Conferma possesso</span>}
                <ChevronRight className="h-4 w-4 text-zinc-400" />
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
